import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Bell, BellOff, BellRing } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export function EnablePush() {
  const supported = "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!supported) return;
    navigator.serviceWorker.ready
      .then((reg) => reg.pushManager.getSubscription())
      .then((sub) => setSubscribed(!!sub))
      .catch(() => {});
  }, [supported]);

  if (!supported) return null;

  const enable = async () => {
    setBusy(true);
    try {
      const perm = await Notification.requestPermission();
      if (perm !== "granted") { toast.error("Notifications refusées dans le navigateur"); return; }
      const { data } = await api.get("/push/vapid-public-key");
      if (!data.public_key) { toast.error("Push non configuré côté serveur"); return; }
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(data.public_key),
      });
      await api.post("/push/subscribe", { subscription: sub.toJSON() });
      setSubscribed(true);
      toast.success("Notifications activées 🔔");
    } catch (err) {
      const denied = err && (err.name === "NotAllowedError" || /denied|permission/i.test(err.message || ""));
      toast.error(denied
        ? "Notifications bloquées par le navigateur. Autorisez-les dans les réglages du site, puis réessayez."
        : "Impossible d'activer les notifications sur cet appareil/navigateur.");
    }
    finally { setBusy(false); }
  };

  const disable = async () => {
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await api.post("/push/unsubscribe", { endpoint: sub.endpoint });
        await sub.unsubscribe();
      }
      setSubscribed(false);
      toast.success("Notifications désactivées");
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };

  return subscribed ? (
    <Button variant="outline" size="sm" className="gap-1 text-xs h-8 border-green-500 text-green-600" onClick={disable} disabled={busy} data-testid="push-disable-btn">
      <BellRing className="h-3.5 w-3.5" /> Alertes ON
    </Button>
  ) : (
    <Button variant="outline" size="sm" className="gap-1 text-xs h-8" onClick={enable} disabled={busy} data-testid="push-enable-btn">
      <Bell className="h-3.5 w-3.5" /> Activer les alertes
    </Button>
  );
}
